import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Textarea, Modal, Spinner, Select } from '../../components';
import MentorAvailabilityModal from '../../components/MentorAvailabilityModal';
import { getCurrentUser, setCurrentUser, clearCurrentUser } from '../../data/session';
import { getMe, updateMe, uploadAvatar, updatePreferences, deleteAccount } from '../../services/user-service';
import { getMentorAvailability, AvailabilitySlot } from '../../services/mentor-availability-service';
import { forgotPassword, friendlyAuthError } from '../../services/auth-service';
import { auth } from '../../config/firebase';

const DEFAULT_NOTIFICATION_PREFS = {
  emailNotifications: true,
  pushNotifications: true,
  weeklyDigest: true,
  mentorUpdates: true,
  courseUpdates: false,
};

const DEFAULT_PRIVACY_PREFS = {
  profileVisibility: true,
  activityStatus: false,
  dataAnalytics: true,
};

const STUDENT_COUNTS = ['<10', '10–50', '50–200', '200+'];
const ASSOC_PURPOSES = ['Academic research', 'Policy analysis', 'Ecosystem reporting', 'Quality control', 'Other'];
const CHECK_SIZES   = ['Angel (<$50k)', 'Seed ($50k–$500k)', 'Series A ($500k–$5M)', 'Later stage'];
const FOUNDER_STAGES = ['Just an idea', 'Prototype', 'MVP in production', 'Revenue'];

const PLAN_PRICE: Record<string, string> = { solo: '$10 one-time', team: '$30 one-time' };
const PLAN_LABEL: Record<string, string> = { solo: 'Solo Founder', team: 'Team' };
const PLAN_FEATURES: Record<string, string[]> = {
  solo: ['Full 6-phase journey', 'Complete LMS access', 'Group mentor sessions', 'Phase certificates', 'Pitch to investors'],
  team: ['Everything in Solo', 'Up to 4 team seats', 'Shared startup profile', 'Cofounder workspace'],
};

interface RoleProfile {
  // Common
  firstName: string; lastName: string; email: string;
  bio: string; company: string; title: string;
  region: string; industry: string;
  // Mentor / Investor
  expertise: string; specialties: string; experience: string;
  // Partner
  institution: string; partnerTitle: string; studentCount: string;
  // Associate
  organization: string; associateTitle: string; purpose: string;
  // Investor extras
  fundName: string; investorTitle: string; checkSize: string;
  // Founder
  founderStage: string; startupName: string;
}

const EMPTY_PROFILE: RoleProfile = {
  firstName: '', lastName: '', email: '',
  bio: '', company: '', title: '',
  region: '', industry: '',
  expertise: '', specialties: '', experience: '',
  institution: '', partnerTitle: '', studentCount: '',
  organization: '', associateTitle: '', purpose: '',
  fundName: '', investorTitle: '', checkSize: '',
  founderStage: '', startupName: '',
};

const splitComma = (s: string) => s.split(',').map(x => x.trim()).filter(Boolean);

const Settings = () => {
  const navigate = useNavigate();
  const user = getCurrentUser();

  const tabs = useMemo(() => {
    const base = [
      { id: 'profile', label: 'Profile' },
      { id: 'account', label: 'Account' },
    ];
    if (user.role === 'founder') base.push({ id: 'plan', label: 'Plan' });
    // base.push({ id: 'notifications', label: 'Notifications' }, { id: 'privacy', label: 'Privacy' });
    return base;
  }, [user.role]);

  const [activeTab, setActiveTab] = useState<string>('profile');

  const [profileData, setProfileData] = useState<RoleProfile>(EMPTY_PROFILE);
  const [adminPermissions, setAdminPermissions] = useState<string[]>([]);
  const [avatarUrl, setAvatarUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  // Snapshots for dirty tracking. Updated on /me load and after each successful save.
  const [initialProfile, setInitialProfile] = useState<RoleProfile>(EMPTY_PROFILE);
  const [initialAvatarUrl, setInitialAvatarUrl] = useState<string>('');
  const isDirty = useMemo(() => {
    const keys = Object.keys(profileData) as (keyof RoleProfile)[];
    for (const k of keys) {
      if (profileData[k] !== initialProfile[k]) return true;
    }
    return avatarUrl !== initialAvatarUrl;
  }, [profileData, initialProfile, avatarUrl, initialAvatarUrl]);

  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetCooldown, setResetCooldown] = useState(0);
  const [isSendingReset, setIsSendingReset] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [notificationSettings, setNotificationSettings] = useState(DEFAULT_NOTIFICATION_PREFS);
  const [privacySettings, setPrivacySettings] = useState(DEFAULT_PRIVACY_PREFS);
  const [isSavingPrefs, setIsSavingPrefs] = useState(false);
  const [prefsSaveSuccess, setPrefsSaveSuccess] = useState<'notifications' | 'privacy' | null>(null);
  const [prefsSaveError, setPrefsSaveError] = useState('');

  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const [showAvailModal, setShowAvailModal]     = useState(false);
  const [availSlots,     setAvailSlots]         = useState<AvailabilitySlot[]>([]);

  const [currentPlan, setCurrentPlan] = useState<'solo' | 'team' | undefined>(user.plan);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  // Detect provider so we can hide reset-password for Google-only users
  const hasPasswordProvider = useMemo(() => {
    const fb = auth.currentUser;
    if (!fb) return true; // assume yes if we can't detect
    return fb.providerData.some(p => p.providerId === 'password');
  }, []);

  useEffect(() => {
    getMe()
      .then(profile => {
        const [firstName, ...rest] = (profile.name || '').split(' ');
        const founder   = (profile as any).founder   || {};
        const mentor    = (profile as any).mentor    || {};
        const partner   = (profile as any).partner   || {};
        const associate = (profile as any).associate || {};
        const investor  = (profile as any).investor  || {};
        const adminSub  = (profile as any).admin     || {};

        const loaded: RoleProfile = {
          firstName,
          lastName: rest.join(' '),
          email: profile.email,
          bio: profile.bio || '',
          company: profile.company || '',
          title: profile.title || '',
          region: profile.region || '',
          industry: (profile.industry || []).join(', '),

          expertise:   (mentor.expertise   || investor.expertise   || profile.expertise   || []).join(', '),
          specialties: (mentor.specialties || investor.specialties || profile.specialties || []).join(', '),
          experience:  mentor.experience || profile.experience || '',

          institution:    partner.institution || '',
          partnerTitle:   partner.titleRole   || '',
          studentCount:   partner.studentCount || '',

          organization:   associate.organization || '',
          associateTitle: associate.titleRole    || '',
          purpose:        associate.purpose      || '',

          fundName:       investor.fundName  || '',
          investorTitle:  investor.titleRole || '',
          checkSize:      investor.checkSize || '',

          founderStage:   founder.stage       || '',
          startupName:    founder.startupName || '',
        };
        setProfileData(loaded);
        setInitialProfile(loaded);
        setAvatarUrl(profile.avatar || '');
        setInitialAvatarUrl(profile.avatar || '');
        setAdminPermissions(adminSub.permissions || profile.permissions || []);
        if ((profile as any).founder?.plan) {
          setCurrentPlan((profile as any).founder.plan);
        }
        if (profile.preferences?.notifications) setNotificationSettings(profile.preferences.notifications);
        if (profile.preferences?.privacy)        setPrivacySettings(profile.preferences.privacy);
        setResetEmail(profile.email);
      })
      .catch(() => {
        const [firstName, ...rest] = (user.name || '').split(' ');
        setProfileData(prev => ({
          ...prev,
          firstName,
          lastName: rest.join(' '),
          bio: user.bio || '',
          company: user.company || '',
          title: user.title || '',
          region: user.region || '',
          industry: (user.industry || []).join(', '),
          expertise: (user.expertise || []).join(', '),
          experience: user.experience || '',
          specialties: (user.specialties || []).join(', '),
        }));
        setAvatarUrl(user.avatar || '');
        if (auth.currentUser?.email) setResetEmail(auth.currentUser.email);
      })
      .finally(() => setIsLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset-cooldown ticker
  useEffect(() => {
    if (resetCooldown <= 0) return;
    const t = setTimeout(() => setResetCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resetCooldown]);

  const initials = `${profileData.firstName.charAt(0)}${profileData.lastName.charAt(0)}`.toUpperCase();

  const handleAvatarFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSaveError('');
    setIsUploadingAvatar(true);
    try {
      const url = await uploadAvatar(file, user.id);
      setAvatarUrl(url);
      // Avatar is auto-saved server-side; update the snapshot too so it doesn't
      // count as a dirty change against the Save Changes button.
      setInitialAvatarUrl(url);
      // Mirror to session so the Topbar avatar updates without a reload.
      setCurrentUser({
        _id: user.id, name: user.name, role: user.role, avatar: url,
        bio: user.bio, company: user.company, title: user.title,
        startupId: user.startupId, isFocalTrainee: user.isFocalTrainee,
        permissions: user.permissions, expertise: user.expertise,
        specialties: user.specialties, experience: user.experience,
        region: user.region, industry: user.industry,
        plan: user.plan, status: user.status, preferences: user.preferences,
        founder: user.founder, partner: user.partner, associate: user.associate, investor: user.investor,
      });
    } catch (err: any) {
      setSaveError(friendlyAuthError(err) || err?.message || 'Failed to upload avatar. Please try again.');
    } finally {
      setIsUploadingAvatar(false);
      // Reset the file input so picking the same file again still triggers the change.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    setSaveError('');
    setSaveSuccess(false);
    try {
      const payload: any = {
        name: `${profileData.firstName} ${profileData.lastName}`.trim(),
        bio: profileData.bio,
        company: profileData.company,
        title: profileData.title,
        region: profileData.region,
        industry: splitComma(profileData.industry),
        avatar: avatarUrl,
      };

      if (user.role === 'founder') {
        payload.founder = {
          stage: profileData.founderStage || undefined,
          startupName: profileData.startupName || undefined,
        };
      }
      if (user.role === 'mentor') {
        payload.mentor = {
          expertise: splitComma(profileData.expertise),
          specialties: splitComma(profileData.specialties),
          experience: profileData.experience,
        };
      }
      if (user.role === 'partner') {
        payload.partner = {
          institution: profileData.institution,
          titleRole: profileData.partnerTitle,
          studentCount: profileData.studentCount || undefined,
        };
      }
      if (user.role === 'associate') {
        payload.associate = {
          organization: profileData.organization,
          titleRole: profileData.associateTitle,
          purpose: profileData.purpose || undefined,
        };
      }
      if (user.role === 'investor') {
        payload.investor = {
          fundName: profileData.fundName,
          titleRole: profileData.investorTitle,
          checkSize: profileData.checkSize || undefined,
          expertise: splitComma(profileData.expertise),
          specialties: splitComma(profileData.specialties),
        };
      }

      const updated = await updateMe(payload);
      setCurrentUser(updated);
      // Re-snapshot so the Save Changes button goes back to disabled.
      setInitialProfile(profileData);
      setInitialAvatarUrl(avatarUrl);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      setSaveError(friendlyAuthError(err) || err?.message || 'Failed to save profile.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendResetEmail = async () => {
    if (resetCooldown > 0) return;
    setResetError('');
    setResetSent(false);
    setIsSendingReset(true);
    try {
      await forgotPassword(resetEmail);
      setResetSent(true);
      setResetCooldown(45);
    } catch (err: any) {
      setResetError(friendlyAuthError(err) || 'Could not send reset email — try again in a moment.');
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleSaveNotifications = async () => {
    setIsSavingPrefs(true);
    setPrefsSaveError('');
    try {
      await updatePreferences({ notifications: notificationSettings });
      setPrefsSaveSuccess('notifications');
      setTimeout(() => setPrefsSaveSuccess(null), 3000);
    } catch (err: any) {
      setPrefsSaveError(err.message || 'Failed to save preferences.');
    } finally {
      setIsSavingPrefs(false);
    }
  };

  const handleSavePrivacy = async () => {
    setIsSavingPrefs(true);
    setPrefsSaveError('');
    try {
      await updatePreferences({ privacy: privacySettings });
      setPrefsSaveSuccess('privacy');
      setTimeout(() => setPrefsSaveSuccess(null), 3000);
    } catch (err: any) {
      setPrefsSaveError(err.message || 'Failed to save privacy settings.');
    } finally {
      setIsSavingPrefs(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeletingAccount(true);
    setDeleteError('');
    try {
      await deleteAccount();
      clearCurrentUser();
      navigate('/login');
    } catch (err: any) {
      setDeleteError(friendlyAuthError(err) || err?.message || 'Failed to delete account.');
    } finally {
      setIsDeletingAccount(false);
      setDeleteConfirm(false);
    }
  };

  const renderProfileTab = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Spinner className="w-6 h-6 text-chart" />
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Avatar row + Save Changes top-right */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 rounded-full overflow-hidden flex-shrink-0">
              {avatarUrl ? (
                <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-chart/20 flex items-center justify-center">
                  <span className="text-2xl text-chart font-bold">{initials || '?'}</span>
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingAvatar}
                >
                  {isUploadingAvatar ? (
                    <span className="inline-flex items-center gap-2"><Spinner className="w-4 h-4" /> Uploading…</span>
                  ) : (
                    'Change Photo'
                  )}
                </Button>
                {user.role === 'mentor' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      try {
                        const doc = await getMentorAvailability();
                        setAvailSlots((doc as any)?.slots || []);
                      } catch {
                        setAvailSlots([]);
                      }
                      setShowAvailModal(true);
                    }}
                  >
                    Change Availability
                  </Button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleAvatarFileChange}
                disabled={isUploadingAvatar}
              />
              <div className="text-muted text-xs mt-1">JPG, PNG, or WEBP up to 5MB</div>
            </div>
          </div>

          <div className="flex flex-col items-stretch sm:items-end gap-2">
            <Button
              variant="primary"
              size="md"
              onClick={handleSaveProfile}
              disabled={!isDirty || isSaving}
            >
              {isSaving ? (
                <span className="inline-flex items-center gap-2"><Spinner className="w-4 h-4" /> Saving…</span>
              ) : (
                'Save Changes'
              )}
            </Button>
            {saveError   && <p className="text-danger  text-xs text-right">{saveError}</p>}
            {saveSuccess && <p className="text-success text-xs text-right">Saved.</p>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="First Name"
            value={profileData.firstName}
            onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
          />
          <Input
            label="Last Name"
            value={profileData.lastName}
            onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
          />
        </div>

        <div>
          <Input label="Email" type="email" value={profileData.email} disabled />
          <p className="text-muted text-xs mt-1">Contact support to change your email.</p>
        </div>

        <Input
          label="Company"
          value={profileData.company}
          onChange={(e) => setProfileData({ ...profileData, company: e.target.value })}
          maxLength={80}
        />

        <Input
          label="Title"
          value={profileData.title}
          onChange={(e) => setProfileData({ ...profileData, title: e.target.value })}
          placeholder="e.g. Founder & CEO"
          maxLength={80}
        />

        <Textarea
          label="Bio"
          value={profileData.bio}
          onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
          rows={4}
          placeholder="Tell us about yourself, your background, and your startup journey..."
          maxLength={500}
          showCount
        />

        <Input
          label="Region"
          value={profileData.region}
          onChange={(e) => setProfileData({ ...profileData, region: e.target.value })}
          placeholder="e.g. Karachi, Pakistan"
        />

        <Input
          label="Industry (comma-separated)"
          value={profileData.industry}
          onChange={(e) => setProfileData({ ...profileData, industry: e.target.value })}
          placeholder="e.g. FinTech, HealthTech"
        />

        {/* Founder-specific */}
        {user.role === 'founder' && (
          <div className="space-y-4 pt-4 border-t border-line">
            <h3 className="heading text-sm text-muted uppercase tracking-wide">Founder Details</h3>
            <Input
              label="Initial idea / startup name"
              value={profileData.startupName}
              onChange={(e) => setProfileData({ ...profileData, startupName: e.target.value })}
              placeholder="One line on what you're building"
              maxLength={100}
            />
            <Select
              label="Current stage"
              placeholder="Select…"
              value={profileData.founderStage}
              onChange={(e) => setProfileData({ ...profileData, founderStage: e.target.value })}
              options={FOUNDER_STAGES.map(s => ({ value: s, label: s }))}
            />
            <p className="text-muted text-xs">Detailed metrics, team, and documents live in your Business Profile.</p>
          </div>
        )}

        {/* Mentor-specific */}
        {user.role === 'mentor' && (
          <div className="space-y-4 pt-4 border-t border-line">
            <h3 className="heading text-sm text-muted uppercase tracking-wide">Mentor Details</h3>
            <Input
              label="Expertise (comma-separated)"
              value={profileData.expertise}
              onChange={(e) => setProfileData({ ...profileData, expertise: e.target.value })}
              placeholder="e.g. SaaS, Growth, Product Strategy"
            />
            <Textarea
              label="Experience"
              value={profileData.experience}
              onChange={(e) => setProfileData({ ...profileData, experience: e.target.value })}
              rows={3}
              placeholder="Describe your professional background..."
            />
            <Input
              label="Specialties (comma-separated)"
              value={profileData.specialties}
              onChange={(e) => setProfileData({ ...profileData, specialties: e.target.value })}
              placeholder="e.g. Fundraising, Go-to-Market, Hiring"
            />
          </div>
        )}

        {/* Partner */}
        {user.role === 'partner' && (
          <div className="space-y-4 pt-4 border-t border-line">
            <h3 className="heading text-sm text-muted uppercase tracking-wide">Institution Details</h3>
            <Input
              label="Institution name"
              value={profileData.institution}
              onChange={(e) => setProfileData({ ...profileData, institution: e.target.value })}
              placeholder="e.g. Institute of Business Administration"
              maxLength={80}
            />
            <Input
              label="Your title / role"
              value={profileData.partnerTitle}
              onChange={(e) => setProfileData({ ...profileData, partnerTitle: e.target.value })}
              placeholder="e.g. Head of Incubation"
              maxLength={80}
            />
            <Select
              label="Approx. student founders"
              placeholder="Select…"
              value={profileData.studentCount}
              onChange={(e) => setProfileData({ ...profileData, studentCount: e.target.value })}
              options={STUDENT_COUNTS.map(o => ({ value: o, label: o }))}
            />
          </div>
        )}

        {/* Associate */}
        {user.role === 'associate' && (
          <div className="space-y-4 pt-4 border-t border-line">
            <h3 className="heading text-sm text-muted uppercase tracking-wide">Organization Details</h3>
            <Input
              label="Organization name"
              value={profileData.organization}
              onChange={(e) => setProfileData({ ...profileData, organization: e.target.value })}
              placeholder="e.g. Pakistan Policy Lab"
              maxLength={80}
            />
            <Input
              label="Role / title"
              value={profileData.associateTitle}
              onChange={(e) => setProfileData({ ...profileData, associateTitle: e.target.value })}
              placeholder="e.g. Senior Researcher"
              maxLength={80}
            />
            <Select
              label="Purpose of access"
              placeholder="Select…"
              value={profileData.purpose}
              onChange={(e) => setProfileData({ ...profileData, purpose: e.target.value })}
              options={ASSOC_PURPOSES.map(o => ({ value: o, label: o }))}
            />
          </div>
        )}

        {/* Investor */}
        {user.role === 'investor' && (
          <div className="space-y-4 pt-4 border-t border-line">
            <h3 className="heading text-sm text-muted uppercase tracking-wide">Fund Details</h3>
            <Input
              label="Fund / firm name"
              value={profileData.fundName}
              onChange={(e) => setProfileData({ ...profileData, fundName: e.target.value })}
              placeholder="e.g. Indus Seed Partners"
              maxLength={80}
            />
            <Input
              label="Role / title"
              value={profileData.investorTitle}
              onChange={(e) => setProfileData({ ...profileData, investorTitle: e.target.value })}
              placeholder="e.g. Principal"
              maxLength={80}
            />
            <Select
              label="Typical check size"
              placeholder="Select…"
              value={profileData.checkSize}
              onChange={(e) => setProfileData({ ...profileData, checkSize: e.target.value })}
              options={CHECK_SIZES.map(o => ({ value: o, label: o }))}
            />
            <Input
              label="Expertise (comma-separated)"
              value={profileData.expertise}
              onChange={(e) => setProfileData({ ...profileData, expertise: e.target.value })}
              placeholder="e.g. SaaS, FinTech"
            />
            <Input
              label="Specialties (comma-separated)"
              value={profileData.specialties}
              onChange={(e) => setProfileData({ ...profileData, specialties: e.target.value })}
              placeholder="e.g. Pre-seed, Seed-stage SaaS"
            />
          </div>
        )}

        {/* Admin — read-only */}
        {user.role === 'admin' && (
          <div className="space-y-3 pt-4 border-t border-line">
            <h3 className="heading text-sm text-muted uppercase tracking-wide">Admin Permissions</h3>
            {adminPermissions.length === 0 ? (
              <p className="text-muted text-sm">No special permissions assigned.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {adminPermissions.map(p => (
                  <span key={p} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-role-admin/10 text-role-admin text-xs font-medium border border-role-admin/30">
                    {p}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    );
  };

  const renderAccountTab = () => (
    <div className="space-y-8">
      {/* Reset Password */}
      <div>
        <h3 className="heading mb-2">Password</h3>
        {hasPasswordProvider ? (
          <>
            <p className="text-muted text-sm mb-4">
              We'll send a password reset link to <strong>{resetEmail}</strong>. Click it to choose a new password.
            </p>
            {resetError   && <p className="text-danger  text-sm mb-3">{resetError}</p>}
            {resetSent    && <p className="text-success text-sm mb-3">Reset email sent — check your inbox (and spam).</p>}
            <Button
              variant="primary"
              size="md"
              onClick={handleSendResetEmail}
              disabled={isSendingReset || resetCooldown > 0}
            >
              {isSendingReset && <Spinner className="w-4 h-4 mr-2" />}
              {isSendingReset
                ? 'Sending…'
                : resetCooldown > 0
                  ? `Resend in ${resetCooldown}s`
                  : resetSent ? 'Send again' : 'Send password reset email'}
            </Button>
          </>
        ) : (
          <div className="rounded-card border border-line bg-surface p-4 text-sm">
            You sign in with Google — there's no password to reset here. Manage your password in your Google account.
          </div>
        )}
      </div>

      {/* Danger Zone */}
      <div className="border-t border-line pt-6">
        <h3 className="heading mb-4 text-danger">Danger Zone</h3>
        <div className="p-4 bg-danger/10 border border-danger/30 rounded-card">
          <div className="text-text font-medium mb-2">Delete Account</div>
          <div className="text-muted text-sm mb-4">
            Once you delete your account there's no going back — all your data will be removed.
          </div>
          {deleteError && <p className="text-danger text-sm mb-3">{deleteError}</p>}
          {deleteConfirm ? (
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                className="text-danger border-danger hover:bg-danger/10"
                onClick={handleDeleteAccount}
                disabled={isDeletingAccount}
              >
                {isDeletingAccount ? (<><Spinner className="w-4 h-4 mr-2" /> Deleting…</>) : 'Yes, delete my account'}
              </Button>
              <button
                className="text-muted text-sm hover:text-text transition-colors"
                onClick={() => setDeleteConfirm(false)}
              >
                Cancel
              </button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="text-danger border-danger hover:bg-danger/10"
              onClick={() => setDeleteConfirm(true)}
            >
              Delete Account
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  const renderPlanTab = () => {
    const planKey = currentPlan || user.plan;
    const planName = planKey ? PLAN_LABEL[planKey] : 'No plan selected';
    const planPrice = planKey ? PLAN_PRICE[planKey] : '';
    const features = planKey ? PLAN_FEATURES[planKey] : [];

    return (
      <div className="space-y-6">
        <div className="rounded-card border border-line bg-shell p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted font-medium mb-1">Current Plan</div>
              <div className="font-display text-3xl tracking-wide text-text">{planName}</div>
              {planPrice && <div className="text-sm text-muted mt-1">{planPrice}</div>}
            </div>
            {!planKey && (
              <Button variant="primary" size="md" onClick={() => navigate('/select-plan')}>
                Pick a plan
              </Button>
            )}
          </div>

          {features.length > 0 && (
            <ul className="mt-6 space-y-2">
              {features.map(f => (
                <li key={f} className="flex items-start gap-2 text-sm text-text">
                  <svg className="w-4 h-4 text-chart flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {planKey && (
          <div>
            <Button variant="primary" size="md" onClick={() => setShowSubscriptionModal(true)}>
              Manage Subscription
            </Button>
          </div>
        )}

        <Modal
          isOpen={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
          title="Subscription management"
        >
          <div className="space-y-3 text-sm text-text">
            <p>Payment management is on its way. We're integrating Stripe so you can:</p>
            <ul className="list-disc pl-5 space-y-1 text-muted">
              <li>Upgrade between Solo and Team</li>
              <li>Update billing details and view invoices</li>
              <li>Manage team seats</li>
            </ul>
            <p className="text-muted">
              For now, your account is active and ready to use. We'll email you the moment payments go live.
            </p>
            <div className="pt-2 flex justify-end">
              <Button variant="primary" size="sm" onClick={() => setShowSubscriptionModal(false)}>
                Got it
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    );
  };

  const renderNotificationsTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="heading mb-4">Email Notifications</h3>
        <div className="space-y-4">
          {Object.entries(notificationSettings).map(([key, value]) => (
            <div key={key} className="flex items-center justify-between">
              <div>
                <div className="text-text font-medium">
                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </div>
                <div className="text-muted text-sm">
                  {key === 'emailNotifications' && 'Receive notifications via email'}
                  {key === 'pushNotifications' && 'Receive push notifications in browser'}
                  {key === 'weeklyDigest' && 'Get a weekly summary of your activity'}
                  {key === 'mentorUpdates' && 'Updates about your mentoring sessions'}
                  {key === 'courseUpdates' && 'Updates about your enrolled courses'}
                </div>
              </div>
              <button
                onClick={() => setNotificationSettings({ ...notificationSettings, [key]: !value })}
                className={`w-12 h-6 rounded-full transition-colors ${value ? 'bg-chart' : 'bg-muted'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full transition-transform ${value ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
          ))}
        </div>
      </div>
      {prefsSaveSuccess === 'notifications' && <p className="text-success text-sm">Notification preferences saved.</p>}
      {prefsSaveError && activeTab === 'notifications' && <p className="text-danger text-sm">{prefsSaveError}</p>}
      <Button variant="primary" onClick={handleSaveNotifications} disabled={isSavingPrefs}>
        {isSavingPrefs ? (<><Spinner className="w-4 h-4 mr-2" /> Saving…</>) : 'Save Preferences'}
      </Button>
    </div>
  );

  const renderPrivacyTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="heading mb-4">Privacy Settings</h3>
        <div className="space-y-4">
          {([
            { key: 'profileVisibility' as const, label: 'Profile Visibility', desc: 'Make your profile visible to other users' },
            { key: 'activityStatus' as const, label: 'Activity Status', desc: "Show when you're online" },
            { key: 'dataAnalytics' as const, label: 'Data Analytics', desc: 'Allow us to use your data for analytics' },
          ]).map(({ key, label, desc }) => (
            <div key={key} className="flex items-center justify-between">
              <div>
                <div className="text-text font-medium">{label}</div>
                <div className="text-muted text-sm">{desc}</div>
              </div>
              <button
                onClick={() => setPrivacySettings({ ...privacySettings, [key]: !privacySettings[key] })}
                className={`w-12 h-6 rounded-full transition-colors ${privacySettings[key] ? 'bg-chart' : 'bg-muted'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full transition-transform ${privacySettings[key] ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
          ))}
        </div>
      </div>
      <div className="border-t border-line pt-6">
        <h3 className="heading mb-4">Data Export</h3>
        <div className="text-text font-medium mb-1">Download Your Data</div>
        <div className="text-muted text-sm mb-4">Download a copy of all your data in JSON format</div>
        <Button variant="ghost" size="sm">Request Data Export</Button>
      </div>
      {prefsSaveSuccess === 'privacy' && <p className="text-success text-sm">Privacy settings saved.</p>}
      {prefsSaveError && activeTab === 'privacy' && <p className="text-danger text-sm">{prefsSaveError}</p>}
      <Button variant="primary" onClick={handleSavePrivacy} disabled={isSavingPrefs}>
        {isSavingPrefs ? (<><Spinner className="w-4 h-4 mr-2" /> Saving…</>) : 'Save Privacy Settings'}
      </Button>
    </div>
  );

  const handleAvailSaved = async () => {
    setShowAvailModal(false);
    try { const fresh = await getMe(); setCurrentUser(fresh); } catch {}
  };

  return (
    <div className="section-gap">
      {showAvailModal && (
        <MentorAvailabilityModal
          initialSlots={availSlots}
          onSaved={handleAvailSaved}
          onClose={() => setShowAvailModal(false)}
        />
      )}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(`/app/dashboard/${user.role}`)}
          className="text-muted hover:text-text transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
        <h2 className="text-2xl font-bold text-text">Settings</h2>
      </div>

      <div className="flex gap-6 border-b border-line overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-chart text-text'
                : 'border-transparent text-muted hover:text-text'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div>
        {activeTab === 'profile'       && renderProfileTab()}
        {activeTab === 'account'       && renderAccountTab()}
        {activeTab === 'plan'          && renderPlanTab()}
        {activeTab === 'notifications' && renderNotificationsTab()}
        {activeTab === 'privacy'       && renderPrivacyTab()}
      </div>
    </div>
  );
};

export default Settings;
